import React, {createContext, useContext} from 'react';

export type ProjectAccessContextType = {
  hasModelsAccess: boolean;
  hasModelsData: boolean;
  hasWeaveAccess: boolean;
  hasWeaveData: boolean;
  isModelsOnly: boolean;
  isWeaveEnterpriseTrialExpired: boolean;
  isWeaveLimitReached: boolean;
  isWeaveOnly: boolean;
};

export const ProjectAccessContext = createContext<
  ProjectAccessContextType | undefined
>(undefined);

type ProjectAccessContextProviderProps = ProjectAccessContextType & {
  children: React.ReactNode;
};

/**
 * This context holds access-related flags that are specific to the
 * ProjectPage and its routes.
 *
 * It is NOT provided in SweepPage or GroupPage contexts — only fields
 * that are needed across all project-like pages belong in
 * ProjectPageContext instead.
 *
 * Why React Context instead of Zustand:
 * These values are stable for the lifetime of a project page visit and
 * change rarely (only when access permissions or feature gates update).
 * When a value does change, the entire subtree SHOULD re-render because
 * a change in access affects what the user can see and interact with.
 */
export const ProjectAccessContextProvider = ({
  children,
  hasModelsAccess,
  hasModelsData,
  hasWeaveAccess,
  hasWeaveData,
  isModelsOnly,
  isWeaveEnterpriseTrialExpired,
  isWeaveLimitReached,
  isWeaveOnly,
}: ProjectAccessContextProviderProps) => {
  const value = {
    hasModelsAccess,
    hasModelsData,
    hasWeaveAccess,
    hasWeaveData,
    isModelsOnly,
    isWeaveEnterpriseTrialExpired,
    isWeaveLimitReached,
    isWeaveOnly,
  };
  // React Compiler caches the context value; this component must stay
  // compiled for that to hold.
  return (
    // oxlint-disable-next-line react/jsx-no-constructed-context-values
    <ProjectAccessContext.Provider value={value}>
      {children}
    </ProjectAccessContext.Provider>
  );
};

export const useProjectAccessContext = () => {
  const context = useContext(ProjectAccessContext);
  if (!context) {
    throw new Error(
      'useProjectAccessContext must be used within a ProjectAccessContextProvider'
    );
  }
  return context;
};

export const useIsModelsOnly = (): boolean => {
  const context = useContext(ProjectAccessContext);
  return !context ? false : context.isModelsOnly;
};
